<template>
  <div class="modal is-active">
    <div class="modal-background"></div>
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">Create new variable</p>
        <button @click="$emit('close')" class="delete" aria-label="close"></button>
      </header>
      <section class="modal-card-body">
        <div class="field">
          <label class="label">Name</label>
          <div class="control">
            <input
              class="input"
              ref="variableInput"
              type="text"
              placeholder="Enter variable name"
              maxlength="50"
              required
              v-model.trim="newVarName"
              @input="$v.newVarName.$touch()"
              :class="{ 'is-danger': $v.newVarName.$invalid && $v.newVarName.$dirty }"
            />
            <p class="help is-danger" v-if="$v.newVarName.$dirty && !$v.newVarName.required">Name is required</p>
            <p class="help is-danger" v-if="$v.newVarName.$dirty && !$v.newVarName.maxLength">
              Name must not be longer than {{ $v.name.$params.maxLength.max }} characters
            </p>
            <p class="help is-danger" v-if="$v.newVarName.$dirty && !$v.newVarName.unique">
              Name is already in use as a flow variable
            </p>
          </div>
        </div>
      </section>
      <footer class="modal-card-foot">
        <n-button-group>
          <n-button variant="primary" :disabled="$v.$invalid" @click="save"> Save </n-button>
          <n-button @click="emit('close')" variant="primary--outline">Cancel</n-button>
        </n-button-group>
      </footer>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { nanoid } from 'nanoid';
import { InputVariableViewModel } from '@/features/bots/models/designer';
import { ADD_FLOW_VARIABLE } from '@/store/bot-designer/mutationTypes';
import { required, maxLength } from '@vuelidate/validators';
import { useStore } from 'vuex';
import { computed, onMounted, ref } from 'vue';
import useVuelidate from '@vuelidate/core';

const emit = defineEmits<{
  (e: 'added', newVariable: InputVariableViewModel): void;
  (e: 'close'): void;
}>();

const store = useStore();
const flowVariables = computed<InputVariableViewModel[]>(() => store.getters.flowVariables);
const addFlowVariable = (flowVar: InputVariableViewModel) => store.commit(ADD_FLOW_VARIABLE, flowVar);

const newVarName = ref('');
const variableInput = ref<HTMLInputElement | null>(null);

const validations = {
  newVarName: {
    required,
    maxLength: maxLength(50),
    unique: (value: string) => !flowVariables.value.some((v) => v.scope === 'flow' && v.name === value),
  },
};

const $v = useVuelidate(validations, { newVarName });

onMounted(() => {
  if (variableInput.value) {
    variableInput.value.focus();
  }
});

function save() {
  if ($v.value.$invalid) {
    return;
  }

  const id = nanoid(7);
  const newVar = {
    id: `var-${id}`,
    scope: 'flow',
    key: newVarName.value,
    name: newVarName.value,
  };

  addFlowVariable(newVar);
  emit('added', newVar);
}
</script>
